import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { postsApi, type BrowseFilters, type CreatePostInput, type HelpOffer } from '@/api/posts';
import { matchingApi } from '@/api/matching';
import type { CommitmentDetail } from '@/api/types';

/** Query keys (data-model.md B2). */
export const helpKeys = {
  posts: (filters: BrowseFilters & { page: number; pageSize: number }) =>
    ['posts', filters] as const,
  allPosts: ['posts'] as const,
  /** Under `allPosts`, so creating, editing, deleting or finishing a post refreshes it too. */
  myPosts: ['posts', 'mine'] as const,
  post: (postId: string) => ['post', postId] as const,
  offers: (postId: string) => ['post', postId, 'offers'] as const,
  /** The offers waiting on the member's own posts (the badge and the counts on post cards). */
  offersWaiting: ['offers-waiting'] as const,
  matches: (postId: string) => ['matches', postId] as const,
  commitment: (commitmentId: string) => ['commitment', commitmentId] as const,
};

export function useBrowsePosts(filters: BrowseFilters, page: number, pageSize: number) {
  return useQuery({
    queryKey: helpKeys.posts({ ...filters, page, pageSize }),
    queryFn: () => postsApi.browsePage(filters, page, pageSize),
    // Keep the current page on screen while the next one loads (no jump to an empty list).
    placeholderData: keepPreviousData,
  });
}

export function usePostDetail(postId: string) {
  return useQuery({ queryKey: helpKeys.post(postId), queryFn: () => postsApi.getDetail(postId) });
}

export function useCreatePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePostInput) => postsApi.create(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: helpKeys.allPosts }),
  });
}

export function useUpdatePost(postId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreatePostInput, 'kind'>) => postsApi.update(postId, input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: helpKeys.allPosts }),
        queryClient.invalidateQueries({ queryKey: helpKeys.post(postId) }),
      ]),
  });
}

export function useDeletePost(postId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => postsApi.remove(postId),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: helpKeys.post(postId) });
      return queryClient.invalidateQueries({ queryKey: helpKeys.allPosts });
    },
  });
}

/** specs/003 #5: the offers on a post (all of them for its poster, the viewer's own otherwise). */
export function useOffers(postId: string) {
  return useQuery({
    queryKey: helpKeys.offers(postId),
    queryFn: () => postsApi.listOffers(postId),
  });
}

/** How often the offers waiting on the member's posts are checked while the app is open. */
export const OFFERS_REFRESH_MS = 30_000;

/** How many offers wait on the member's own posts, how many are new, and on which post. */
export function useOffersWaiting() {
  return useQuery({
    queryKey: helpKeys.offersWaiting,
    queryFn: postsApi.offersWaiting,
    refetchInterval: OFFERS_REFRESH_MS,
    refetchOnWindowFocus: true,
  });
}

/** Offer, withdraw, accept or decline; the offers are reloaded afterwards. */
export function useOfferAction<Args>(postId: string, action: (args: Args) => Promise<HelpOffer>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: action,
    onSettled: () => queryClient.invalidateQueries({ queryKey: helpKeys.offers(postId) }),
  });
}

export function useReleaseCommitment(postId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commitmentId: string) => matchingApi.release(commitmentId),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: helpKeys.post(postId) }),
        queryClient.invalidateQueries({ queryKey: helpKeys.allPosts }),
      ]),
  });
}

export function useCandidates(postId: string) {
  return useQuery({
    queryKey: helpKeys.matches(postId),
    queryFn: () => matchingApi.getCandidates(postId),
  });
}

/** How often an open commitment page checks for the other neighbour's changes (no socket event exists for them). */
export const COMMITMENT_REFRESH_MS = 10_000;

export function useCommitment(commitmentId: string) {
  return useQuery({
    queryKey: helpKeys.commitment(commitmentId),
    queryFn: () => matchingApi.getCommitment(commitmentId),
    refetchInterval: COMMITMENT_REFRESH_MS,
    refetchOnWindowFocus: true,
  });
}

/** A commitment change: refetch it (and the post it locks) so both sides see the new state. */
export function useCommitmentAction<Args>(
  commitmentId: string,
  action: (args: Args) => Promise<unknown>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: action,
    onSuccess: (result) => {
      // Show the server's answer at once; the refetch below fills in the rest.
      const updated = result as Partial<CommitmentDetail> | null;
      if (updated && typeof updated === 'object' && updated.id === commitmentId) {
        queryClient.setQueryData<CommitmentDetail>(helpKeys.commitment(commitmentId), (current) =>
          current ? { ...current, ...stripUndefined(updated) } : current,
        );
      }
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: helpKeys.commitment(commitmentId) }),
        queryClient.invalidateQueries({ queryKey: ['post'] }),
        queryClient.invalidateQueries({ queryKey: helpKeys.allPosts }),
      ]);
    },
  });
}

function stripUndefined<T extends object>(value: T): Partial<T> {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as Partial<T>;
}
